import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Globe, Loader2, Eye, ExternalLink, Map } from "lucide-react";
import ImageUpload from "../../components/admin/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const themes = [
  { value: "business", label: "Business (Performance Pro)" },
  { value: "politics", label: "Politics (Urgent Earth)" },
  { value: "tech", label: "Technology" },
  { value: "sports", label: "Sports" },
  { value: "entertainment", label: "Entertainment" },
];

const defaultCategories = ["business", "politics", "technology", "entertainment", "sports", "health"];

const AdminProjects = () => {
  const { getAuthHeaders } = useAuth();
  const { projects, refreshProjects, selectProject } = useProject();
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    theme: "business",
    categories: defaultCategories,
    description: "",
    domain: "",
    db_name: "",
    logo: "",
    favicon: "",
    google_analytics_id: "",
    google_tag_manager_id: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadProjects = async () => {
      await refreshProjects();
      setLoading(false);
    };
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      theme: "business",
      categories: defaultCategories,
      description: "",
      domain: "",
      db_name: "",
      logo: "",
      favicon: "",
      google_analytics_id: "",
      google_tag_manager_id: "",
    });
    setEditingProject(null);
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        name: project.name,
        slug: project.slug,
        theme: project.theme,
        categories: project.categories,
        description: project.description || "",
        domain: project.domain || "",
        db_name: project.db_name || "",
        logo: project.logo || "",
        favicon: project.favicon || "",
        google_analytics_id: project.google_analytics_id || "",
        google_tag_manager_id: project.google_tag_manager_id || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate db_name for new projects
    if (!editingProject && !formData.db_name) {
      toast.error("Database name is required");
      return;
    }
    
    setSubmitting(true);

    try {
      if (editingProject) {
        // Don't send db_name when editing
        const { db_name, ...updateData } = formData;
        await axios.put(
          `${API}/projects/${editingProject.id}`,
          updateData,
          { headers: getAuthHeaders() }
        );
        toast.success("Project updated successfully");
      } else {
        const response = await axios.post(
          `${API}/projects`,
          formData,
          { headers: getAuthHeaders() }
        );
        selectProject(response.data);
        toast.success("Project created with database: " + formData.db_name);
      }
      await refreshProjects();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save project");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;

    try {
      await axios.delete(`${API}/projects/${projectId}`, { headers: getAuthHeaders() });
      toast.success("Project deleted");
      await refreshProjects();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  return (
    <AdminLayout title="Projects">
      <div className="space-y-6" data-testid="admin-projects">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[#64748B]">Manage your news websites</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} data-testid="create-project-button">
                <Plus className="h-4 w-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-3xl max-h-[92vh] p-0 gap-0 overflow-hidden flex flex-col">
              <DialogHeader className="px-5 sm:px-6 py-4 border-b border-slate-200 shrink-0">
                <DialogTitle>{editingProject ? "Edit Project" : "Create New Project"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Project Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        name: e.target.value,
                        slug: editingProject ? formData.slug : generateSlug(e.target.value)
                      });
                    }}
                    placeholder="Business Daily"
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug (URL identifier)</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="business-daily"
                    required
                    disabled={!!editingProject}
                    data-testid="project-slug-input"
                  />
                </div>
                {!editingProject && (
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="db_name">Database Name *</Label>
                    <Input
                      id="db_name"
                      value={formData.db_name}
                      onChange={(e) => setFormData({ ...formData, db_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                      placeholder="news_businessdaily"
                      required
                      data-testid="project-dbname-input"
                    />
                    <p className="text-xs text-[#64748B]">Separate MongoDB database for this website (e.g., news_businessdaily)</p>
                  </div>
                )}
                {editingProject && formData.db_name && (
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Database</Label>
                    <p className="text-sm text-[#64748B] bg-[#F1F5F9] px-3 py-2 rounded">{formData.db_name}</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="theme">Theme</Label>
                  <Select
                    value={formData.theme}
                    onValueChange={(value) => setFormData({ ...formData, theme: value })}
                  >
                    <SelectTrigger data-testid="project-theme-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {themes.map((theme) => (
                        <SelectItem key={theme.value} value={theme.value}>
                          {theme.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Custom Domain (optional)</Label>
                  <Input
                    id="domain"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    placeholder="news.example.com"
                    data-testid="project-domain-input"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the news website"
                    rows={2}
                    data-testid="project-description-input"
                  />
                </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Logo</Label>
                    <ImageUpload
                      value={formData.logo}
                      onChange={(url) => setFormData({ ...formData, logo: url })}
                      label="Upload logo"
                      aspect="square"
                      testId="project-logo-upload"
                    />
                    <p className="text-xs text-slate-500">Used for SEO Organization schema.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Favicon</Label>
                    <ImageUpload
                      value={formData.favicon}
                      onChange={(url) => setFormData({ ...formData, favicon: url })}
                      label="Upload favicon"
                      accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml"
                      aspect="square"
                      testId="project-favicon-upload"
                    />
                    <p className="text-xs text-slate-500">ICO / PNG / SVG. Browser tabs.</p>
                  </div>
                </div>
                <div className="space-y-4 rounded-lg border border-slate-200 p-4 bg-slate-50">
                  <div>
                    <Label className="text-sm font-semibold text-[#0F172A]">Google Analytics / Tag Manager</Label>
                    <p className="text-xs text-slate-500 mt-1">GA4 Measurement ID (<code className="px-1 rounded bg-white">G-</code>) and/or GTM container ID (<code className="px-1 rounded bg-white">GTM-</code>). Verified via GA Realtime / Tag Assistant.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ga_id">GA4 Measurement ID</Label>
                      <Input
                        id="ga_id"
                        value={formData.google_analytics_id}
                        onChange={(e) => setFormData({ ...formData, google_analytics_id: e.target.value.trim() })}
                        placeholder="G-XXXXXXXXXX"
                        data-testid="project-ga-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gtm_id">GTM Container ID (optional)</Label>
                      <Input
                        id="gtm_id"
                        value={formData.google_tag_manager_id}
                        onChange={(e) => setFormData({ ...formData, google_tag_manager_id: e.target.value.trim() })}
                        placeholder="GTM-XXXXXXX"
                        data-testid="project-gtm-id-input"
                      />
                    </div>
                  </div>
                </div>
                </div>
                <div className="flex justify-end gap-3 px-5 sm:px-6 py-4 border-t border-slate-200 bg-white shrink-0">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="save-project-button">
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {editingProject ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="border-0 shadow-sm animate-pulse">
                <CardContent className="p-6 h-48" />
              </Card>
            ))}
          </div>
        ) : projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <Card key={project.id} className="border-0 shadow-sm card-hover" data-testid={`project-card-${project.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                      <p className="text-sm text-[#64748B] mt-1">/{project.slug}</p>
                    </div>
                    <Badge variant="secondary" className="capitalize">
                      {project.theme}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {project.db_name && (
                    <div className="flex items-center gap-2 text-xs text-[#64748B] mb-3 bg-[#F1F5F9] px-2 py-1 rounded">
                      <span className="font-mono">DB: {project.db_name}</span>
                    </div>
                  )}
                  {project.description && (
                    <p className="text-sm text-[#64748B] mb-4 line-clamp-2">{project.description}</p>
                  )}
                  {project.domain && (
                    <div className="flex items-center gap-2 text-sm text-[#64748B] mb-4">
                      <Globe className="h-4 w-4" />
                      {project.domain}
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    {project.categories.slice(0, 3).map((cat) => (
                      <Badge key={cat} variant="outline" className="text-xs">
                        {cat}
                      </Badge>
                    ))}
                    {project.categories.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{project.categories.length - 3}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/?preview=${project.slug}`, "_blank")}
                      title="Preview inside this admin app"
                      data-testid={`preview-project-${project.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    {project.domain && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`https://${project.domain}`, "_blank", "noopener,noreferrer")}
                        title={`Open ${project.domain} in a new tab`}
                        data-testid={`visit-project-${project.id}`}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Visit Site
                      </Button>
                    )}
                    {project.domain && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`https://${project.domain}/sitemap.xml`, "_blank", "noopener,noreferrer")}
                        title={`Open sitemap.xml for ${project.domain}`}
                        data-testid={`sitemap-project-${project.id}`}
                      >
                        <Map className="h-4 w-4 mr-1" />
                        Sitemap
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(project)}
                      data-testid={`edit-project-${project.id}`}
                    >
                      <Edit2 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(project.id)}
                      data-testid={`delete-project-${project.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <Globe className="h-12 w-12 mx-auto mb-4 text-[#64748B] opacity-50" />
              <h3 className="font-semibold text-[#0F172A] mb-2">No projects yet</h3>
              <p className="text-[#64748B] mb-4">Create your first news website project</p>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Create Project
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProjects;
